import React, { useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import '../assets/styles/AnalysisPage.css';

const AnalysisPage: React.FC = () => {
  const triumvirateRef = useRef<HTMLElement>(null);

  // Load Vimeo script on component mount
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://player.vimeo.com/api/player.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  // Function to scroll to Chart Triumvirate section
  const scrollToTriumvirate = () => {
    triumvirateRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Inline styles to avoid CSS conflicts
  const optionsStyles = {
    section: {
      padding: '64px 0',
      backgroundColor: '#ffffff',
      borderTop: '1px solid rgba(179, 150, 86, 0.15)',
      borderBottom: '1px solid rgba(179, 150, 86, 0.15)',
      position: 'relative' as const,
      zIndex: 1,
      clear: 'both' as const,
      display: 'block',
      width: '100%',
      boxSizing: 'border-box' as const,
    },
    container: {
      width: '100%',
      maxWidth: '1140px',
      margin: '0 auto',
      padding: '0 20px',
      boxSizing: 'border-box' as const,
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: window.innerWidth < 768 ? '1fr' : '1fr 1fr',
      gap: window.innerWidth < 768 ? '24px' : '32px',
      width: '100%',
      maxWidth: '900px',
      margin: '0 auto',
      boxSizing: 'border-box' as const,
    },
    card: {
      backgroundColor: '#ffffff',
      borderRadius: '12px',
      padding: window.innerWidth < 768 ? '24px' : '32px',
      boxShadow: '0 5px 15px rgba(0, 0, 0, 0.05)',
      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
      height: 'auto',
      display: 'flex',
      flexDirection: 'column' as const,
      border: '1px solid rgba(179, 150, 86, 0.1)',
      boxSizing: 'border-box' as const,
      width: '100%',
    },
    title: {
      fontSize: '20px',
      marginTop: 0,
      marginBottom: '16px',
      color: '#333333',
      position: 'relative' as const,
      display: 'inline-block',
      fontWeight: 600,
    },
    description: {
      color: '#666666',
      lineHeight: 1.6,
      marginBottom: '24px',
      flexGrow: 1,
      fontSize: '16px',
    },
    primaryBtn: {
      marginTop: 'auto',
      alignSelf: 'flex-start' as const,
      display: 'inline-block',
      padding: '10px 20px',
      backgroundColor: '#00694a',
      color: 'white',
      borderRadius: '6px',
      fontWeight: 500,
      border: 'none',
      cursor: 'pointer',
      transition: 'background-color 0.3s ease',
      textDecoration: 'none',
      fontSize: '16px',
      textAlign: 'center' as const,
    },
    secondaryBtn: {
      marginTop: 'auto',
      alignSelf: 'flex-start' as const,
      display: 'inline-block',
      padding: '10px 20px',
      backgroundColor: '#b39656',
      color: 'white',
      borderRadius: '6px',
      fontWeight: 500,
      border: 'none',
      cursor: 'pointer',
      transition: 'background-color 0.3s ease',
      textDecoration: 'none',
      fontSize: '16px',
      textAlign: 'center' as const,
    },
    highlightRed: {
      color: '#e53e3e',
    }
  };

  // Add a state for tracking window width for responsive design
  const [windowWidth, setWindowWidth] = React.useState(window.innerWidth);

  // Effect to handle window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    
    // Clean up event listener
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Compute responsive styles based on current window width
  const responsiveGridStyles = {
    ...optionsStyles.grid,
    gridTemplateColumns: windowWidth < 768 ? '1fr' : '1fr 1fr',
    gap: windowWidth < 768 ? '24px' : '32px',
  };

  const responsiveCardStyles = {
    ...optionsStyles.card,
    padding: windowWidth < 768 ? '24px' : '32px',
  };

  return (
    <div className="analysis-page">
      <Helmet>
        <title>Stock Analysis | Price on Rails | Translate Stocks</title>
        <meta 
          name="description" 
          content="Get immediate stock analysis reports or create your own custom algorithms with TranslateStocks' powerful analysis tools."
        />
      </Helmet>

      {/* Hero Section */}
      <section className="analysis-hero">
        <div className="container">
          <div className="analysis-header">
            <div className="infinity-icon">∞</div>
            <h1>
              <span className="highlight">Stock Analysis</span> Solutions by <span className="highlight">TranslateStocks</span>
            </h1>
            <h2>Choose the analysis approach that works for you</h2>
          </div>
        </div>
      </section>

      {/* Analysis Options Section with inline styles */}
      <section style={optionsStyles.section}>
        <div style={optionsStyles.container}>
          <div style={responsiveGridStyles}>
            <div style={responsiveCardStyles}>
              <h3 style={optionsStyles.title}>
                Ready-to-Use Stock Analysis <span style={optionsStyles.highlightRed}>(FREE)</span>
              </h3>
              <p style={optionsStyles.description}>
                Daily stock reports with support/resistance levels. No technical knowledge required.
              </p>
              <a 
                href="https://www.translatestocks.com/research" 
                style={optionsStyles.primaryBtn}
              >
                View Stock Analysis
              </a>
            </div>

            <div style={responsiveCardStyles}>
              <h3 style={optionsStyles.title}>
                Custom Algorithm Development
              </h3>
              <p style={optionsStyles.description}>
                Build trading algorithms with our cloud platform. Connect to APIs and integrate with Python.
              </p>
              <button 
                onClick={scrollToTriumvirate} 
                style={optionsStyles.secondaryBtn}
              >
                More Details
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Stock Analysis Section */}
      <section className="stock-analysis-section">
        <div className="container">
          <div className="stock-analysis-header">
            <h2>Stay Ahead of the Market with <span className="highlight">Daily Stock Analysis</span></h2>
            <h3>at Your Fingertips. Actual Charts From Last Close Available Now:</h3>
            <p>
              Transform Your Investment Strategy with The Interpreter<br />
              Analyze the entire S&P 500 at Close and Get Insights and Supports/Resistances.
            </p>
          </div>
          
          <div className="stock-grid">
            <div className="stock-card">
              <a href="https://www.translatestocks.com/stock-analysis-TSLA" className="stock-link">
                <div className="stock-image">
                  <img 
                    src="https://www.translatestocks.com/lastanalysis/TSLA.webp" 
                    alt="Tesla Stock Analysis" 
                    className="stock-chart"
                  />
                  <img 
                    src="https://www.translatestocks.com/ico/TSLA.png" 
                    alt="Tesla Logo" 
                    className="stock-logo"
                  />
                </div>
                <h4>TESLA</h4>
              </a>
            </div>
            
            <div className="stock-card">
              <a href="https://www.translatestocks.com/stock-analysis-AAPL" className="stock-link">
                <div className="stock-image">
                  <img 
                    src="https://www.translatestocks.com/lastanalysis/AAPL.webp" 
                    alt="Apple Stock Analysis" 
                    className="stock-chart"
                  />
                  <img 
                    src="https://www.translatestocks.com/ico/AAPL.png" 
                    alt="Apple Logo" 
                    className="stock-logo"
                  />
                </div>
                <h4>APPLE</h4>
              </a>
            </div>
            
            <div className="stock-card">
              <a href="https://www.translatestocks.com/stock-analysis-AMZN" className="stock-link">
                <div className="stock-image">
                  <img 
                    src="https://www.translatestocks.com/lastanalysis/AMZN.webp" 
                    alt="Amazon Stock Analysis" 
                    className="stock-chart"
                  />
                  <img 
                    src="https://www.translatestocks.com/ico/AMZN.png" 
                    alt="Amazon Logo" 
                    className="stock-logo"
                  />
                </div>
                <h4>AMAZON</h4>
              </a>
            </div>
            
            <div className="stock-card">
              <a href="https://www.translatestocks.com/stock-analysis-NVDA" className="stock-link">
                <div className="stock-image">
                  <img 
                    src="https://www.translatestocks.com/lastanalysis/NVDA.webp" 
                    alt="Nvidia Stock Analysis" 
                    className="stock-chart"
                  />
                  <img 
                    src="https://www.translatestocks.com/ico/NVDA.png" 
                    alt="Nvidia Logo" 
                    className="stock-logo"
                  />
                </div>
                <h4>NVIDIA</h4>
              </a>
            </div>
            
            <div className="stock-card">
              <a href="https://www.translatestocks.com/stock-analysis-AMD" className="stock-link">
                <div className="stock-image">
                  <img 
                    src="https://www.translatestocks.com/lastanalysis/AMD.webp" 
                    alt="AMD Stock Analysis" 
                    className="stock-chart"
                  />
                  <img 
                    src="https://www.translatestocks.com/ico/AMD.png" 
                    alt="AMD Logo" 
                    className="stock-logo"
                  />
                </div>
                <h4>AMD</h4>
              </a>
            </div>
            
            <div className="stock-card">
              <a href="https://www.translatestocks.com/stock-analysis-MSFT" className="stock-link">
                <div className="stock-image">
                  <img 
                    src="https://www.translatestocks.com/lastanalysis/MSFT.webp" 
                    alt="Microsoft Stock Analysis" 
                    className="stock-chart"
                  />
                  <img 
                    src="https://www.translatestocks.com/ico/MSFT.png" 
                    alt="Microsoft Logo" 
                    className="stock-logo"
                  />
                </div>
                <h4>MICROSOFT</h4>
              </a>
            </div>
          </div>
          
          <div className="more-link">
            <a href="https://www.translatestocks.com/research" className="btn btn-primary">View More Analysis</a>
          </div>
        </div>
      </section>

      {/* Chart Triumvirate Section */}
      <section ref={triumvirateRef} className="triumvirate-section">
        <div className="container">
          <h2 className="section-title">The Chart Triumvirate</h2>
          <p className="section-subtitle">Our advanced Cloud Platform for developing customized trading algorithms and strategies</p>
          
          <div className="triumvirate-grid">
            <div className="triumvirate-item">
              <h3>1. The Programming Language</h3>
              <div className="video-container">
                <div style={{ padding: '56.25% 0 0 0', position: 'relative' }}>
                  <iframe 
                    src="https://player.vimeo.com/video/790406266?h=946d4e9e94&badge=0&autopause=0&player_id=0&app_id=58479" 
                    frameBorder="0" 
                    allow="autoplay; fullscreen; picture-in-picture" 
                    allowFullScreen 
                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                    title="TS-Script"
                  ></iframe>
                </div>
              </div>
            </div>

            <div className="triumvirate-item">
              <h3>2. The BackTest Lab</h3>
              <div className="video-container">
                <div style={{ padding: '56.25% 0 0 0', position: 'relative' }}>
                  <iframe 
                    src="https://player.vimeo.com/video/789646827?h=efc7c0da41&badge=0&autopause=0&player_id=0&app_id=58479" 
                    frameBorder="0" 
                    allow="autoplay; fullscreen; picture-in-picture" 
                    allowFullScreen 
                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                    title="Backtest Lab"
                  ></iframe>
                </div>
              </div>
            </div>

            <div className="triumvirate-item">
              <h3>3. The Robot Charts</h3>
              <div className="video-container">
                <div style={{ padding: '56.25% 0 0 0', position: 'relative' }}>
                  <iframe 
                    src="https://player.vimeo.com/video/791343226?h=0513e3800c&badge=0&autopause=0&player_id=0&app_id=58479" 
                    frameBorder="0" 
                    allow="autoplay; fullscreen; picture-in-picture" 
                    allowFullScreen 
                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                    title="robots-final2"
                  ></iframe>
                </div>
              </div>
            </div>
          </div>
          
          <div className="triumvirate-cta">
            <Link to="/pricing" className="btn btn-secondary">View Cloud Platform Pricing</Link>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="features-grid-section">
        <div className="container">
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M19.5 3H4.5C3.12 3 2 4.12 2 5.5V18.5C2 19.88 3.12 21 4.5 21H19.5C20.88 21 22 19.88 22 18.5V5.5C22 4.12 20.88 3 19.5 3ZM4.5 5H19.5C19.78 5 20 5.22 20 5.5V7H4V5.5C4 5.22 4.22 5 4.5 5ZM19.5 19H4.5C4.22 19 4 18.78 4 18.5V9H20V18.5C20 18.78 19.78 19 19.5 19Z" fill="currentColor"/>
                </svg>
              </div>
              <h3>The Interpreter</h3>
              <p>
                Watch in Awe as Fibonacci, Triangles, and Head and Shoulder Figures are Unveiled in Real-Time - Hit the Play Button.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 3H11V11H3V3ZM13 3H21V11H13V3ZM3 13H11V21H3V13ZM16 13H18V16H21V18H18V21H16V18H13V16H16V13Z" fill="currentColor"/>
                </svg>
              </div>
              <h3>BackTest Lab</h3>
              <p>
                Unlock the Secrets of the Perfect Buy/Sell Signals with our exclusive backtesting - Use it and discover the Best Results.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM17 13H13V17H11V13H7V11H11V7H13V11H17V13Z" fill="currentColor"/>
                </svg>
              </div>
              <h3>Trading Robots</h3>
              <p>
                Transform Your Trading Experience with the Power of Automation - Launch your Trading Robots and Monitor their Performance on the Go!
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M4 6H20V8H4V6ZM4 11H20V13H4V11ZM4 16H20V18H4V16Z" fill="currentColor"/>
                </svg>
              </div>
              <h3>Desktop + Web Applications</h3>
              <p>
                Experience the Best of Both Worlds with our Revolutionary Desktop and Mobile Platform - Convenience and Power Unbeatable!
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M17 18C15.89 18 15 18.89 15 20C15 21.11 15.89 22 17 22C18.11 22 19 21.11 19 20C19 18.89 18.11 18 17 18ZM1 2V4H3L6.6 11.59L5.25 14.04C5.09 14.32 5 14.65 5 15C5 16.11 5.89 17 7 17H19V15H7.42C7.28 15 7.17 14.89 7.17 14.75C7.17 14.7 7.18 14.66 7.2 14.63L8.1 13H15.55C16.3 13 16.96 12.59 17.3 11.97L20.88 5.5C20.96 5.34 21 5.17 21 5C21 4.45 20.55 4 20 4H5.21L4.27 2H1ZM7 18C5.89 18 5 18.89 5 20C5 21.11 5.89 22 7 22C8.11 22 9 21.11 9 20C9 18.89 8.11 18 7 18Z" fill="currentColor"/>
                </svg>
              </div>
              <h3>Rent Your Strategies</h3>
              <p>
                Revolutionize Your Business with Our Platform - Create Your Own Robo-Like Business by Renting and Selling Your Robots and Algorithms.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 3V9H9V3H3ZM7 7H5V5H7V7ZM3 11V17H9V11H3ZM7 15H5V13H7V15ZM11 3V9H17V3H11ZM15 7H13V5H15V7ZM11 11V17H17V11H11ZM15 15H13V13H15V15Z" fill="currentColor"/>
                </svg>
              </div>
              <h3>Whitelabel Solutions</h3>
              <p>
                Achieve Business Success with Our Customizable Platform - Create Unlimited Bots and Serve Unlimited Clients Under Your Own Brand!
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AnalysisPage;